import {
  Component,
  Input,
  DestroyRef,
  ViewChild,
  OnInit,
  OnChanges,
  OnDestroy,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Chart, ChartConfiguration, Plugin } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { ThemeService } from 'src/app/core/services/theme.service';

@Component({
  selector: 'app-patch-compliance-gauge',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './patch-compliance-gauge.component.html',
  styleUrls: ['./patch-compliance-gauge.component.scss'],
})
export class PatchComplianceGaugeComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) compliancePct = 0;

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;

  gaugeChartData: ChartConfiguration<'doughnut'>['data'] = { datasets: [] };
  gaugeChartOptions: ChartConfiguration<'doughnut'>['options'] = {};

  private valueColor = '#555555';
  private trackColor = '#E4E8ED';

  readonly gaugeChartPlugins: Plugin[] = [
    {
      id: 'patchComplianceGaugeCenterText',
      afterDatasetsDraw: (chart: Chart) => {
        const { ctx, chartArea } = chart;
        if (!chartArea) return;
        const cx = (chartArea.left + chartArea.right) / 2;
        const midline = (chartArea.top + chartArea.bottom) / 2;
        ctx.save();
        ctx.font = '800 24px Muli, Arial, Helvetica, sans-serif';
        ctx.fillStyle = this.valueColor;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'top';
        ctx.fillText(`${this.compliancePct}%`, cx, midline + 8);
        ctx.font = '600 11px Muli, Arial, Helvetica, sans-serif';
        ctx.fillText('Compliant', cx, midline + 34);
        ctx.font = '600 10px Muli, Arial, Helvetica, sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText('0%', chartArea.left + 10, midline + 54);
        ctx.textAlign = 'right';
        ctx.fillText('100%', chartArea.right - 10, midline + 54);
        ctx.restore();
      },
    },
  ];

  constructor(
    private themeService: ThemeService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit(): void {
    this.buildChartOptions();
    this.buildChartData();
    this.themeService.themeSubject$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.buildChartOptions();
      this.buildChartData();
      this.chart?.update();
    });
  }

  ngOnChanges(): void {
    this.buildChartData();
    this.chart?.update();
  }

  ngOnDestroy(): void {
    Chart.unregister(...this.gaugeChartPlugins);
  }

  private buildChartData(): void {
    this.gaugeChartData = {
      datasets: [
        {
          data: [this.compliancePct, 100 - this.compliancePct],
          backgroundColor: ['#00c100', this.trackColor],
          borderWidth: 0,
        },
      ],
    };
  }

  private buildChartOptions(): void {
    const isDark = this.themeService.isInDarkMode();
    this.valueColor = isDark ? '#F1F1F1' : '#555555';
    this.trackColor = isDark ? '#2c3743' : '#E4E8ED';

    this.gaugeChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      circumference: 180,
      rotation: -90,
      cutout: '72%',
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
    };
  }
}
